import { useCallback } from 'react';
import { useClient } from 'urql';

import { FeatureFlag } from '@revelio/core';
import type {
  CompanyDiscoveryAiOutput,
  TalentDiscoveryAiFilterSearchResponseV2,
} from '@revelio/data-access';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import {
  usePageConfig,
  useTabContentConfig,
} from '../../components/PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../components/PageConfigProvider/types';
import { isScreenerV2Page } from '../../utils/routing-helpers';
import { createDefaultFilterMap } from '../contexts/defaultFilters';
import { useFilterContext } from '../contexts/FilterProvider';
import { roleTaxonomyDefinition } from '../definitions/roleTaxonomy';
import {
  generatePageKey,
  NO_PRIMARY_FILTER_KEY,
} from '../engine/filterGroupConsistency';
import { useAiSearchCompatibilityToast } from '../hooks/useAiSearchCompatibilityToast';
import { useAllSelectionLists } from '../hooks/useAllSelectionLists';
import {
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  isMultiFilter,
  type MultiFilter,
  type TreeFilter,
} from '../types/filters';
import { patchTreeFilterNames } from '../utils/patchTreeFilterNames';
import { adaptAiSearchFiltersForPage } from './adaptAiSearchFiltersForPage';
import {
  collectUnresolvedCompanyRcids,
  resolveCompanyNamesFromApi,
} from './resolveUnknownCompanyNames';
import { transformAiResponseToCustomDashFilters } from './transformAiResponseToCustomDashFilters';
import type { AiTransformUnknownFilter } from './transformAiResponseToCustomDashFilters/types';
import type { ScreenerAiTransformUnknownFilter } from './transformScreenerAiResponseToCustomDashFilters';
import { transformScreenerAiResponseToCustomDashFilters } from './transformScreenerAiResponseToCustomDashFilters';

export type ApplyAiSearchFiltersResult = {
  adaptedFilters: Filter[];
  unknownFilters: AiTransformUnknownFilter[];
};

export type ApplyScreenerAiSearchFiltersResult = {
  adaptedFilters: Filter[];
  unknownFilters: ScreenerAiTransformUnknownFilter[];
};

const isMultiCompanyFilter = (
  filter: Filter
): filter is MultiFilter<TreeFilter> =>
  isMultiFilter(filter) && filter.wrappedFilterId === FilterId.COMPANY;

const getConsistencyKeyForAiSearch = (
  revelioPageId: RevelioPageId | string | undefined,
  primaryFilterConfig: { id: string } | undefined
): string => {
  if (isScreenerV2Page(revelioPageId) && revelioPageId) {
    return revelioPageId;
  }
  return primaryFilterConfig?.id ?? NO_PRIMARY_FILTER_KEY;
};

/**
 * Returns a callback that applies an AI Search response to the filter state:
 * transform -> role taxonomy injection -> base filter map -> adapt -> resolve names -> dispatch.
 * Callers can use the returned { adaptedFilters, unknownFilters } for context-specific
 * updates (e.g. AiSearchContext state, highlighting overlay).
 */
export const useApplyAiSearchFilters = () => {
  const {
    dispatch,
    getFilterData,
    activeFilterMap,
    defaultSelectionOverrides,
    filterState,
  } = useFilterContext();
  const selectionLists = useAllSelectionLists();
  const gqlClient = useClient();
  const { revelioPageId, primaryFilter: primaryFilterConfig } =
    useTabContentConfig();
  const { config: pageConfig } = usePageConfig();
  const showCompatibilityToast = useAiSearchCompatibilityToast();

  const applyAiSearchFilters = useCallback(
    async (
      response: TalentDiscoveryAiFilterSearchResponseV2,
      options?: {
        /** Defaults to `merge` so AI primary selections combine with page defaults (overflow deactivated). Pass `replace` to wipe defaults. */
        primarySelectionMergeMode?: 'merge' | 'replace';
        /**
         * When true, do not coerce an unknown `revelioPageId` to Talent Discovery
         * inside `adaptAiSearchFiltersForPage` (e.g. global-assistant chart preview).
         */
        skipTalentDiscoveryAdaptFallback?: boolean;
      }
    ): Promise<ApplyAiSearchFiltersResult> => {
      const { filters: customDashFilters, unknownFilters } =
        transformAiResponseToCustomDashFilters(
          response,
          selectionLists,
          getFilterData
        );

      const hasRoleInstance = customDashFilters.some(
        (f) => f.id === FilterId.ROLE || f.id === FilterId.MULTI_ROLE
      );
      const hasRoleTaxonomyInstance = customDashFilters.some(
        (f) => f.id === FilterId.ROLE_TAXONOMY
      );

      // IMPORTANT: when resolving taxonomy filters to carry over into this
      // apply, read from the TARGET group's filter state — NOT from
      // `activeFilterMap` (which is the currently-ACTIVE group's map, and
      // during navigation is still the previous page's group). Using
      // `activeFilterMap` causes a cross-scope leak: e.g. navigating
      // COMPANY → GEOGRAPHY writes `{default: true, isUserSubmitted: true}`
      // into the GEOGRAPHY group via pref sync; then when the user
      // navigates back to COMPANY, `activeFilterMap` is still the GEOGRAPHY
      // group, we'd preserve its `{default: true, isUserSubmitted: true}`
      // into the REPLACE_FILTERS payload targeting the COMPANY group, and
      // `filtersToPreferences` on COMPANY then infers NOT-adaptive (because
      // `isUserSubmitted: true`), making the Preferences UI report the
      // user's COMPANY pref as "default" even though it's actually
      // "adaptive" on the server.
      //
      // Reading from the target group preserves each group's own taxonomy
      // state independently, and falls back to `baseFilterMap` defaults
      // when the group hasn't been visited yet.
      const targetGroupFilterMap = primaryFilterConfig?.id
        ? filterState.groupMap.get(primaryFilterConfig.id)
        : undefined;

      if (hasRoleInstance && !hasRoleTaxonomyInstance) {
        const existingRoleTaxonomy = targetGroupFilterMap?.get(
          FilterId.ROLE_TAXONOMY
        );
        const hasUsableExistingTaxonomy =
          existingRoleTaxonomy &&
          existingRoleTaxonomy.type === FilterType.VALUE &&
          'value' in existingRoleTaxonomy;
        customDashFilters.push(
          hasUsableExistingTaxonomy
            ? // Preserve the target group's existing filter AS-IS —
              // critically including `isUserSubmitted`. Flipping it to
              // `true` locks the page out of adaptive mode
              // (preference-filter-adapters infers adaptive from
              // `!isUserSubmitted && COMPANY scope`), which
              // (a) makes the Preferences modal display the concrete
              //     resolved taxonomy instead of "Adaptive" and
              // (b) stops the adaptive hooks from recomputing on company
              //     selection changes.
              existingRoleTaxonomy
            : {
                ...roleTaxonomyDefinition,
                // For a freshly-injected taxonomy (no existing target
                // group state) the filter IS genuinely user-submitted by
                // the AI apply.
                isUserSubmitted: true,
              }
        );
      }

      const baseFilterMap = createDefaultFilterMap(
        primaryFilterConfig?.id,
        undefined,
        defaultSelectionOverrides
      );
      [FilterId.ROLE_TAXONOMY, FilterId.SKILL_TAXONOMY].forEach((filterId) => {
        const existing = targetGroupFilterMap?.get(filterId);
        if (existing) {
          baseFilterMap.set(filterId, existing);
        }
      });

      const consistencyKey = getConsistencyKeyForAiSearch(
        revelioPageId,
        primaryFilterConfig
      );
      const pageKey = generatePageKey(
        consistencyKey,
        revelioPageId ?? pageConfig.id
      );

      const currentPrimaryFilter = (() => {
        if (!primaryFilterConfig) return undefined;
        // Same reasoning as above: read the target group's primary filter
        // state, not the active (mid-nav, still previous scope's) map.
        const existing =
          targetGroupFilterMap?.get(primaryFilterConfig.id) ??
          baseFilterMap.get(primaryFilterConfig.id);
        if (
          !existing ||
          existing.type !== FilterType.TREE ||
          !existing.isPrimary
        ) {
          return existing;
        }
        const pageSelections = filterState.pageMap
          .get(pageKey)
          ?.get(primaryFilterConfig.id);
        if (!pageSelections) return existing;
        return { ...existing, primarySelections: pageSelections };
      })();

      const adaptRevelioPageId =
        options?.skipTalentDiscoveryAdaptFallback === true
          ? revelioPageId
          : (revelioPageId ?? RevelioPageId.TALENT_DISCOVERY);

      const { adaptedFilters, modifications } = adaptAiSearchFiltersForPage({
        filters: customDashFilters,
        revelioPageId: adaptRevelioPageId,
        primaryFilterConfig,
        currentPrimaryFilter,
        primarySelectionMergeMode:
          options?.primarySelectionMergeMode ?? 'merge',
      });

      if (modifications.length > 0) {
        showCompatibilityToast(modifications);
      }

      const filterMap: FilterSelectionsMap = new Map(baseFilterMap);
      adaptedFilters.forEach((filter) => {
        filterMap.set(filter.id, filter);
      });

      const unresolvedRcids = collectUnresolvedCompanyRcids(adaptedFilters);

      if (unresolvedRcids.length > 0) {
        try {
          const nameMap = await resolveCompanyNamesFromApi(
            gqlClient,
            unresolvedRcids
          );

          if (nameMap.size > 0) {
            const companyFilterId = filterMap.has(FilterId.MULTI_COMPANY)
              ? FilterId.MULTI_COMPANY
              : FilterId.COMPANY;
            const companyFilter = filterMap.get(companyFilterId);

            if (companyFilter) {
              filterMap.set(
                companyFilterId,
                isMultiCompanyFilter(companyFilter)
                  ? {
                      ...companyFilter,
                      instances: companyFilter.instances.map((inst) =>
                        patchTreeFilterNames(inst, nameMap)
                      ),
                    }
                  : patchTreeFilterNames(companyFilter, nameMap)
              );
            }
          }
        } catch (err) {
          console.warn(
            '[AI Search] Failed to resolve company names from API, falling back to numeric IDs',
            err
          );
        }
      }

      dispatch({
        type: 'REPLACE_FILTERS',
        payload: {
          filterMap,
          pageKey,
          skipReconciliation: true,
        },
      });

      const mergedUnknownFilters = [
        ...(response.unknownFilters || []),
        ...unknownFilters,
      ];

      const resolvedFilters = adaptedFilters.map(
        (f) => filterMap.get(f.id) ?? f
      );

      return {
        adaptedFilters: resolvedFilters,
        unknownFilters: mergedUnknownFilters,
      };
    },
    [
      selectionLists,
      getFilterData,
      defaultSelectionOverrides,
      filterState,
      primaryFilterConfig,
      revelioPageId,
      pageConfig.id,
      showCompatibilityToast,
      dispatch,
      gqlClient,
    ]
  );

  const isValuationEnabled = useUnleashFlag(
    FeatureFlag.CompanyDiscoveryValuationFilter
  );
  const applyScreenerAiSearchFilters = useCallback(
    (
      response: CompanyDiscoveryAiOutput
    ): ApplyScreenerAiSearchFiltersResult => {
      const { filters: rawCustomDashFilters, unknownFilters } =
        transformScreenerAiResponseToCustomDashFilters(response);

      const customDashFilters = isValuationEnabled
        ? rawCustomDashFilters
        : rawCustomDashFilters.filter((f) => f.id !== FilterId.VALUATION);

      const baseFilterMap = createDefaultFilterMap(
        primaryFilterConfig?.id,
        undefined,
        defaultSelectionOverrides
      );
      [FilterId.ROLE_TAXONOMY, FilterId.SKILL_TAXONOMY].forEach((filterId) => {
        const existing = activeFilterMap.get(filterId);
        if (existing) {
          baseFilterMap.set(filterId, existing);
        }
      });

      const consistencyKey = getConsistencyKeyForAiSearch(
        revelioPageId,
        primaryFilterConfig
      );
      const pageKey = generatePageKey(
        consistencyKey,
        revelioPageId ?? pageConfig.id
      );

      const filterMap: FilterSelectionsMap = new Map(baseFilterMap);
      customDashFilters.forEach((filter) => {
        filterMap.set(filter.id, filter);
      });

      dispatch({
        type: 'REPLACE_FILTERS',
        payload: {
          filterMap,
          pageKey,
          skipReconciliation: true,
        },
      });

      return {
        adaptedFilters: customDashFilters,
        unknownFilters,
      };
    },
    [
      activeFilterMap,
      defaultSelectionOverrides,
      primaryFilterConfig,
      revelioPageId,
      pageConfig.id,
      dispatch,
      isValuationEnabled,
    ]
  );

  return { applyAiSearchFilters, applyScreenerAiSearchFilters };
};
